import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { registerUser, loginUser, forgotPassword } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent } from '../ui/card';
import { Mail, Lock, User, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

// Login / register / forgot-password card shown in the landing hero.
export const AuthCard = ({ authMode, setAuthMode }) => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: '', password: '', name: '' });
  const [loading, setLoading] = useState(false);

  const handleGoogle = () => {
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.password) { toast.error('Email et mot de passe obligatoires'); return; }
    if (authMode === 'register' && !form.name) { toast.error('Le nom est obligatoire'); return; }
    if (form.password.length < 6) { toast.error('Le mot de passe doit contenir au moins 6 caractères'); return; }
    setLoading(true);
    try {
      let res;
      if (authMode === 'register') {
        res = await registerUser({ email: form.email, password: form.password, name: form.name });
        toast.success('Compte créé avec succès !');
      } else {
        res = await loginUser({ email: form.email, password: form.password });
        toast.success('Connexion réussie');
      }
      login(res.data);
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur de connexion');
    }
    setLoading(false);
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!form.email) { toast.error('Entrez votre adresse email'); return; }
    setLoading(true);
    try {
      await forgotPassword(form.email);
      toast.success('Si un compte existe, un email de réinitialisation a été envoyé.');
      setAuthMode('login');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de l\'envoi');
    }
    setLoading(false);
  };

  const content = authMode === 'forgot' ? (
        <Card className="bg-[#0d1117] border border-white/[0.06] shadow-2xl shadow-black/60 animate-fade-in w-full max-w-md" data-testid="auth-card">
          <CardContent className="p-6 md:p-8">
            <h2 className="text-xl font-black mb-1">Mot de passe oublié</h2>
            <p className="text-sm text-muted-foreground mb-6">Entrez votre email pour recevoir un lien de réinitialisation.</p>
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div><Label className="text-sm">Email</Label><div className="relative"><Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" /><Input data-testid="forgot-email" type="email" placeholder="prof@college.fr" value={form.email} onChange={(e) => setForm(p => ({ ...p, email: e.target.value }))} className="pl-10" /></div></div>
              <Button type="submit" data-testid="forgot-submit-btn" disabled={loading} className="w-full bg-blue-600 text-white hover:bg-blue-700 h-11">
                {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Envoi...</> : 'Envoyer le lien'}
              </Button>
            </form>
            <p className="text-center text-sm text-muted-foreground mt-4">
              <button onClick={() => setAuthMode('login')} className="text-blue-400 hover:underline" data-testid="back-to-login">Retour à la connexion</button>
            </p>
          </CardContent>
        </Card>
  ) : authMode ? (
    <Card className="bg-[#0d1117] border border-white/[0.06] shadow-2xl shadow-black/60 animate-fade-in w-full max-w-md" data-testid="auth-card">
      <CardContent className="p-6 md:p-8">
        <h2 className="text-xl font-black mb-1">{authMode === 'register' ? 'Créer un compte' : 'Se connecter'}</h2>
        <p className="text-sm text-muted-foreground mb-6">{authMode === 'register' ? 'Inscrivez-vous pour créer vos QCM.' : 'Accédez à votre espace enseignant.'}</p>
        <Button data-testid="google-login-btn" onClick={handleGoogle} variant="secondary" className="w-full mb-4 border border-white/10 h-11">
          <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
          Continuer avec Google
        </Button>
        <div className="relative my-5"><div className="absolute inset-0 flex items-center"><div className="w-full border-t border-white/[0.06]" /></div><div className="relative flex justify-center text-xs"><span className="bg-[#0d1117] px-3 text-muted-foreground">ou par email</span></div></div>
        <form onSubmit={handleSubmit} className="space-y-4">
          {authMode === 'register' && (
            <div><Label className="text-sm">Nom complet</Label><div className="relative"><User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" /><Input data-testid="auth-name" placeholder="Jean Dupont" value={form.name} onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))} className="pl-10" /></div></div>
          )}
          <div><Label className="text-sm">Email</Label><div className="relative"><Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" /><Input data-testid="auth-email" type="email" placeholder="prof@college.fr" value={form.email} onChange={(e) => setForm(p => ({ ...p, email: e.target.value }))} className="pl-10" /></div></div>
          <div>
            <div className="flex items-center justify-between"><Label className="text-sm">Mot de passe</Label>{authMode === 'login' && <button type="button" onClick={() => setAuthMode('forgot')} className="text-xs text-blue-400 hover:underline" data-testid="forgot-password-link">Mot de passe oublié ?</button>}</div>
            <div className="relative"><Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" /><Input data-testid="auth-password" type="password" placeholder="6 caractères minimum" value={form.password} onChange={(e) => setForm(p => ({ ...p, password: e.target.value }))} className="pl-10" /></div>
          </div>
          <Button type="submit" data-testid="auth-submit-btn" disabled={loading} className="w-full bg-blue-600 text-white hover:bg-blue-700 h-11">
            {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Chargement...</> : authMode === 'register' ? 'Créer mon compte' : 'Se connecter'}
          </Button>
        </form>
        <p className="text-center text-sm text-muted-foreground mt-4">
          {authMode === 'register' ? (<>Déjà un compte ? <button onClick={() => setAuthMode('login')} className="text-blue-400 hover:underline" data-testid="switch-to-login">Se connecter</button></>) : (<>Pas encore de compte ? <button onClick={() => setAuthMode('register')} className="text-blue-400 hover:underline" data-testid="switch-to-register">S'inscrire</button></>)}
        </p>
      </CardContent>
    </Card>
  ) : null;

  return content;
};
